import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, formatMoney, PAYMENT_METHODS, PAYMENT_LABEL } from "@/lib/api";
import { Plus, Trash2, Receipt, FileText, Upload, Eye, Calendar, TrendingUp, TrendingDown, Calculator, Download, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, PieChart, Pie, Cell,
} from "recharts";

const EXPENSE_CATEGORIES = [
  "insumos", "servicios", "nómina", "alquiler", "marketing",
  "mantenimiento", "impuestos", "honorarios", "transporte", "general",
];

const REPORT_COLORS = ["hsl(var(--primary))", "hsl(var(--accent))", "hsl(var(--chart-3))", "hsl(var(--chart-4))", "hsl(var(--chart-5))"];

const TABS = [
  { key: "ventas", label: "Ventas", icon: TrendingUp },
  { key: "gastos", label: "Gastos", icon: TrendingDown },
  { key: "reportes", label: "Reportes", icon: BarChart3 },
];

function todayStr() { return new Date().toISOString().slice(0, 10); }
function firstOfMonth() {
  const d = new Date(); d.setDate(1);
  return d.toISOString().slice(0, 10);
}

function ExpenseForm({ open, onClose, onSaved, expense }) {
  const isNew = !expense?.id;
  const [form, setForm] = useState({
    date: todayStr(), amount: "", category: "insumos",
    supplier: "", description: "", payment_method: "efectivo",
    invoice_number: "", notes: "",
  });
  const [savedId, setSavedId] = useState(null);
  const [hasReceipt, setHasReceipt] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (isNew) {
      setForm({ date: todayStr(), amount: "", category: "insumos",
        supplier: "", description: "", payment_method: "efectivo",
        invoice_number: "", notes: "" });
      setSavedId(null);
      setHasReceipt(false);
    } else {
      setForm({
        date: expense.date || "", amount: expense.amount || 0,
        category: expense.category || "general",
        supplier: expense.supplier || "", description: expense.description || "",
        payment_method: expense.payment_method || "efectivo",
        invoice_number: expense.invoice_number || "", notes: expense.notes || "",
      });
      setSavedId(expense.id);
      setHasReceipt(!!expense.receipt_path);
    }
  }, [open, expense, isNew]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (isNew) {
        const { data } = await api.post("/expenses", payload);
        setSavedId(data.id);
        toast.success("Gasto registrado");
      } else {
        await api.put(`/expenses/${expense.id}`, payload);
        toast.success("Gasto actualizado");
        onSaved && onSaved();
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!savedId) { toast.error("Guarda el gasto primero"); return; }
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/expenses/${savedId}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Comprobante adjuntado");
      setHasReceipt(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight text-2xl">
            {isNew ? "Nuevo gasto" : "Editar gasto"}
          </SheetTitle>
        </SheetHeader>
        <form onSubmit={submit} className="space-y-4 mt-6 pb-10" data-testid="expense-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">Fecha *</label>
              <input data-testid="exp-date" required type="date" className="input-flat font-mono" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Monto *</label>
              <input data-testid="exp-amount" required type="number" step="1000" className="input-flat font-mono font-bold" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Categoría *</label>
            <select data-testid="exp-category" required className="input-flat" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Proveedor</label>
            <input data-testid="exp-supplier" className="input-flat" placeholder="Razón social o nombre" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-2">Forma de pago</label>
              <select className="input-flat" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
                {PAYMENT_METHODS.map((m) => <option key={m.key} value={m.key}>{m.short}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">N° factura</label>
              <input data-testid="exp-invoice" className="input-flat font-mono" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Descripción</label>
            <input data-testid="exp-description" className="input-flat" placeholder="Detalle del gasto" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Notas para el contador</label>
            <textarea rows={2} className="input-flat h-auto py-2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          <div className="border border-border rounded-sm p-4 bg-muted/30 space-y-3">
            <div className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-primary" />
              <div className="font-display font-bold text-sm">Factura o comprobante</div>
            </div>
            {!savedId ? (
              <p className="text-xs text-muted-foreground">Guarda primero el gasto para poder adjuntar la factura.</p>
            ) : hasReceipt ? (
              <div className="flex items-center justify-between gap-2 p-2 bg-card border border-border rounded-sm">
                <div className="flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-primary" />
                  <span className="font-semibold truncate">{expense?.receipt_filename || "Comprobante adjunto"}</span>
                </div>
                <button type="button" onClick={() => fileRef.current?.click()} className="text-xs font-bold uppercase tracking-widest text-primary hover:underline">
                  Reemplazar
                </button>
              </div>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary !h-10 w-full justify-center" data-testid="exp-upload-btn">
                <Upload className="w-4 h-4" /> {uploading ? "Subiendo…" : "Adjuntar factura/foto"}
              </button>
            )}
            <input ref={fileRef} type="file" onChange={onUpload} className="hidden" accept="image/*,application/pdf" />
            <p className="text-[10px] text-muted-foreground">Formatos aceptados: JPG, PNG, PDF. Útil para entregar al contador a fin de mes.</p>
          </div>

          <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background/95 border-t border-border backdrop-blur flex gap-2">
            <button type="submit" className="btn-primary flex-1 justify-center" data-testid="exp-save">
              {isNew && !savedId ? "Guardar gasto" : "Guardar cambios"}
            </button>
            {savedId && (
              <button type="button" onClick={() => { onSaved && onSaved(); onClose(); }} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-sm">
                Cerrar
              </button>
            )}
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function ExpensesTab({ from, to }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const token = localStorage.getItem("df_token");

  const load = async () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    const { data } = await api.get("/expenses", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [from, to]); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar gasto?")) return;
    try { await api.delete(`/expenses/${id}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const filtered = filter
    ? items.filter((i) => `${i.supplier} ${i.description} ${i.category} ${i.invoice_number}`.toLowerCase().includes(filter.toLowerCase()))
    : items;

  const total = filtered.reduce((s, i) => s + (i.amount || 0), 0);

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const headers = ["fecha", "categoria", "proveedor", "factura", "descripcion", "metodo", "monto", "notas", "tiene_comprobante"];
    const rows = filtered.map((i) => [
      i.date, i.category, i.supplier || "", i.invoice_number || "",
      (i.description || "").replace(/"/g, '""'),
      i.payment_method || "", i.amount,
      (i.notes || "").replace(/"/g, '""'),
      i.receipt_path ? "sí" : "no",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `gastos_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          placeholder="Buscar proveedor, factura, descripción…"
          value={filter} onChange={(e) => setFilter(e.target.value)}
          className="input-flat flex-1 min-w-[260px]"
          data-testid="exp-search"
        />
        <button onClick={exportCsv} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2" data-testid="exp-export-btn">
          <Download className="w-4 h-4" /> Exportar
        </button>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary" data-testid="new-expense-btn">
          <Plus className="w-4 h-4" /> Nuevo gasto
        </button>
      </div>

      <div className="card-flat p-4 flex items-center justify-between">
        <div>
          <div className="label-eyebrow">Total gastos en el período</div>
          <div className="font-mono font-bold text-3xl mt-1 text-destructive">{formatMoney(total)}</div>
        </div>
        <div className="text-right">
          <div className="label-eyebrow">Registros</div>
          <div className="font-mono font-bold text-2xl mt-1">{filtered.length}</div>
        </div>
      </div>

      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-2">Categoría</div>
          <div className="col-span-2">Proveedor</div>
          <div className="col-span-3">Descripción</div>
          <div className="col-span-1">Comp.</div>
          <div className="col-span-1 text-right">Monto</div>
          <div className="col-span-1 text-right">Acciones</div>
        </div>
        {filtered.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No hay gastos registrados en el período.</div>
        )}
        {filtered.map((it) => {
          const url = it.receipt_path ? `${process.env.REACT_APP_BACKEND_URL}/api/expenses/${it.id}/receipt/download${token ? `?auth=${token}` : ""}` : null;
          return (
            <div key={it.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/40" data-testid={`exp-row-${it.id}`}>
              <div className="col-span-2 font-mono text-xs">{it.date}</div>
              <div className="col-span-2 text-xs uppercase tracking-widest font-bold">{it.category}</div>
              <div className="col-span-2 text-sm truncate">{it.supplier || "—"}</div>
              <div className="col-span-3 text-sm truncate">{it.description || "—"}{it.invoice_number ? <span className="text-muted-foreground font-mono ml-2 text-xs">#{it.invoice_number}</span> : null}</div>
              <div className="col-span-1">
                {url ? (
                  <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-widest hover:underline" data-testid={`exp-view-${it.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Ver
                  </a>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">sin</span>
                )}
              </div>
              <div className="col-span-1 text-right font-mono font-bold text-destructive">- {formatMoney(it.amount)}</div>
              <div className="col-span-1 flex justify-end gap-1">
                <button onClick={() => { setEditing(it); setOpen(true); }} className="p-2 text-muted-foreground hover:text-primary" title="Editar"><FileText className="w-4 h-4" /></button>
                {(user?.role === "admin" || user?.role === "recepcionista") && (
                  <button onClick={() => remove(it.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ExpenseForm open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} expense={editing} />
    </div>
  );
}

function SalesTab({ from, to }) {
  const [items, setItems] = useState([]);
  const [filterMethod, setFilterMethod] = useState("");

  const load = async () => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (filterMethod) params.payment_method = filterMethod;
    const { data } = await api.get("/sales", { params });
    setItems(data);
  };
  useEffect(() => { load(); }, [from, to, filterMethod]); // eslint-disable-line

  const total = items.reduce((s, i) => s + (i.amount || 0), 0);
  const byMethod = {};
  for (const t of items) {
    const m = t.payment_method || "efectivo";
    byMethod[m] = (byMethod[m] || 0) + t.amount;
  }

  const exportCsv = () => {
    if (items.length === 0) return;
    const headers = ["fecha", "hora", "paciente", "tratamiento", "metodo", "monto", "descripcion"];
    const rows = items.map((i) => [
      i.session_date, new Date(i.created_at).toLocaleTimeString("es-CO"),
      i.patient?.name || "", i.treatment_name || "",
      i.payment_method || "", i.amount, (i.description || "").replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ventas_${from}_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="sales-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <select className="input-flat w-48" value={filterMethod} onChange={(e) => setFilterMethod(e.target.value)} data-testid="sales-method-filter">
          <option value="">Todas las formas</option>
          {PAYMENT_METHODS.map((m) => <option key={m.key} value={m.key}>{m.short}</option>)}
        </select>
        <button onClick={exportCsv} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2" data-testid="sales-export-btn">
          <Download className="w-4 h-4" /> Exportar
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card-flat p-4">
          <div className="label-eyebrow">Total ventas</div>
          <div className="font-mono font-bold text-3xl mt-1 text-primary">{formatMoney(total)}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Transacciones</div>
          <div className="font-mono font-bold text-3xl mt-1">{items.length}</div>
        </div>
        <div className="card-flat p-4">
          <div className="label-eyebrow">Ticket promedio</div>
          <div className="font-mono font-bold text-3xl mt-1">{formatMoney(items.length ? total / items.length : 0)}</div>
        </div>
      </div>

      {/* Por método */}
      {Object.keys(byMethod).length > 0 && (
        <div className="card-flat p-4">
          <div className="label-eyebrow mb-3">Ventas por método de pago</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {PAYMENT_METHODS.filter(m => byMethod[m.key]).map((m) => (
              <div key={m.key} className="border border-border rounded-sm p-3" style={{ borderColor: m.bg }}>
                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: m.bg }}>{m.short}</div>
                <div className="font-mono font-bold text-lg mt-1">{formatMoney(byMethod[m.key])}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-border bg-muted/40 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          <div className="col-span-2">Fecha</div>
          <div className="col-span-3">Paciente</div>
          <div className="col-span-3">Tratamiento</div>
          <div className="col-span-2">Forma pago</div>
          <div className="col-span-2 text-right">Monto</div>
        </div>
        {items.length === 0 && (
          <div className="px-4 py-12 text-center text-sm text-muted-foreground">No hay ventas en el período.</div>
        )}
        {items.map((s) => (
          <div key={s.id} className="grid grid-cols-12 px-4 py-3 border-b border-border last:border-b-0 items-center hover:bg-muted/40" data-testid={`sale-row-${s.id}`}>
            <div className="col-span-2 font-mono text-xs">
              <div>{s.session_date}</div>
              <div className="text-muted-foreground">{new Date(s.created_at).toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })}</div>
            </div>
            <div className="col-span-3 text-sm truncate">{s.patient?.name || s.description || "—"}</div>
            <div className="col-span-3 text-sm text-muted-foreground truncate">{s.treatment_name || "—"}</div>
            <div className="col-span-2">
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm bg-muted">{PAYMENT_LABEL[s.payment_method] || s.payment_method || "—"}</span>
            </div>
            <div className="col-span-2 text-right font-mono font-bold text-primary">+ {formatMoney(s.amount)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportsTab() {
  const [period, setPeriod] = useState("weekly");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports", { params: { period } }).then((r) => setData(r.data));
  }, [period]);

  const exportCsv = () => {
    if (!data?.series?.length) return;
    const headers = ["fecha", "ingreso", "gasto", "balance"];
    const rows = data.series.map((r) => [r.date, r.ingreso, r.gasto, r.balance]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c ?? ""}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `reporte-${period}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) return <div className="text-sm text-muted-foreground py-12 text-center" data-testid="reports-loading">Cargando reporte…</div>;

  return (
    <div className="space-y-4" data-testid="reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs font-mono text-muted-foreground">Período: {data.start} → {data.end}</div>
        <div className="flex flex-wrap gap-2">
          <div className="flex border border-border rounded-sm overflow-hidden">
            {[
              { k: "daily", l: "Hoy" },
              { k: "weekly", l: "7 días" },
              { k: "monthly", l: "30 días" },
            ].map((p) => (
              <button
                key={p.k} onClick={() => setPeriod(p.k)}
                data-testid={`period-${p.k}`}
                className={`px-3 h-10 text-xs uppercase tracking-widest font-bold ${period === p.k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>
                {p.l}
              </button>
            ))}
          </div>
          <button onClick={exportCsv} className="px-4 h-10 rounded-sm border border-border hover:bg-muted text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2" data-testid="reports-export-csv">
            <Download className="w-4 h-4" /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card-flat p-5">
          <div className="label-eyebrow">Ingresos</div>
          <div className="font-mono font-bold text-3xl mt-2 text-primary">{formatMoney(data.totals.income)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="label-eyebrow">Gastos</div>
          <div className="font-mono font-bold text-3xl mt-2 text-destructive">{formatMoney(data.totals.expense)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="label-eyebrow">Balance</div>
          <div className="font-mono font-bold text-3xl mt-2">{formatMoney(data.totals.balance)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-flat p-5 lg:col-span-2">
          <h3 className="font-display font-bold text-xl tracking-tight mb-3">Movimientos por día</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                  formatter={(v) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="ingreso" fill="hsl(var(--primary))" radius={[2,2,0,0]} />
                <Bar dataKey="gasto" fill="hsl(var(--destructive))" radius={[2,2,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-flat p-5">
          <h3 className="font-display font-bold text-xl tracking-tight mb-3">Gastos por categoría</h3>
          {data.categories.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Sin gastos en el período.</div>
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.categories} dataKey="amount" nameKey="category" outerRadius={90} innerRadius={50} paddingAngle={2}>
                    {data.categories.map((_, i) => <Cell key={i} fill={REPORT_COLORS[i % REPORT_COLORS.length]} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 4, fontSize: 12 }}
                    formatter={(v) => formatMoney(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Contabilidad() {
  const [tab, setTab] = useState("ventas");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayStr());
  const [summary, setSummary] = useState(null);

  const loadSummary = async () => {
    const { data } = await api.get("/accounting/summary", { params: { date_from: from, date_to: to } });
    setSummary(data);
  };
  useEffect(() => { loadSummary(); }, [from, to]); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="contabilidad-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2"><Calculator className="w-3 h-3" /> Contabilidad</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">Libro contable</h1>
          <p className="text-muted-foreground text-sm mt-3">Ventas, gastos y comprobantes para el contador.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <div className="label-eyebrow mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Desde</div>
            <input type="date" className="input-flat font-mono w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="period-from" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Hasta</div>
            <input type="date" className="input-flat font-mono w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="period-to" />
          </div>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="card-flat p-5 border-l-4 border-l-primary">
            <div className="label-eyebrow">Ingresos del período</div>
            <div className="font-mono font-bold text-3xl mt-1 text-primary">{formatMoney(summary.income)}</div>
            <div className="text-xs text-muted-foreground mt-1">{summary.sales_count} ventas</div>
          </div>
          <div className="card-flat p-5 border-l-4 border-l-destructive">
            <div className="label-eyebrow">Gastos del período</div>
            <div className="font-mono font-bold text-3xl mt-1 text-destructive">{formatMoney(summary.expenses)}</div>
            <div className="text-xs text-muted-foreground mt-1">{summary.expenses_count} registros</div>
          </div>
          <div className={`card-flat p-5 border-l-4 ${summary.net >= 0 ? "border-l-primary" : "border-l-destructive"}`}>
            <div className="label-eyebrow">Utilidad neta</div>
            <div className={`font-mono font-bold text-3xl mt-1 ${summary.net >= 0 ? "text-primary" : "text-destructive"}`}>
              {formatMoney(summary.net)}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Ingresos − Gastos</div>
          </div>
        </div>
      )}

      <div className="flex border-b border-border">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-5 h-11 text-sm font-semibold inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${active ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "ventas" && <SalesTab from={from} to={to} />}
      {tab === "gastos" && <ExpensesTab from={from} to={to} />}
      {tab === "reportes" && <ReportsTab />}
    </div>
  );
}
